import { Box, Flex, Text, Tooltip, useMantineTheme } from '@mantine/core';

export type ContributionRow = {
  campaign_id: string;
  campaign_name: string;
  contributed: number;
  exclusive: number;
  shared: number;
  contribution_pct: number;
  contributed_revenue: number;
  exclusive_revenue: number;
  spend: number;
};

export type ContributionData = {
  eligible: boolean;
  reason: string;
  total: number;
  goal_name: string;
  has_revenue: boolean;
  goal_value: number;
  total_revenue?: number;
  rows: ContributionRow[];
  // Selected campaigns without the primary goal; they have no bar.
  excluded?: string[];
};

type ContributionChartProps = {
  data: ContributionData | null;
  isDarkTheme?: boolean;
};

const NOTE =
  'How many converting customers each campaign reached, and how much could be lost if it were removed. '
  + 'A shared customer appears in the bar of every campaign they visited, so the bars add up to more than '
  + 'the total, and the percentages can add up to more than 100%.';

// Mantine palette keys; warm hues left out so no campaign reads as an error/loss state.
const SERIES_COLORS = ['blue', 'teal', 'violet', 'cyan', 'grape', 'indigo', 'green', 'pink'];
const SHARED_INK = '#1a1b1e';

const money = (value: number) =>
  (value < 0 ? '-$' : '$') + Math.abs(Math.round(value)).toLocaleString();

const plural = (n: number) => (n === 1 ? 'conversion' : 'conversions');

// A real but tiny share must not round down to a misleading 0.00%.
const pctLabel = (row: ContributionRow) =>
  row.contributed > 0 && row.contribution_pct < 0.01 ? '<0.01%' : `${Number(row.contribution_pct).toFixed(2)}%`;

// Shared-only sentence (E = 0, C > 0), used by the hover tooltip.
const sharedOnlySentence = (row: ContributionRow, hasRevenue: boolean) => {
  const c = row.contributed;
  const worth = hasRevenue ? ` worth ${money(row.contributed_revenue)}` : '';
  const cost = c === 1 ? 'that sale' : 'those sales';
  return `${row.campaign_name} contributed to ${c} ${plural(c)}${worth}. Removing it could cost you ${cost}.`;
};

// Full "Potential Loss" sentence for the hover tooltip. Wording is state-dependent, chosen by
// comparing exclusive (E) to contributed (C). Revenue clauses appear only when the goal has a value.
const lossSentence = (row: ContributionRow, hasRevenue: boolean) => {
  const c = row.contributed;
  const e = row.exclusive;
  const name = row.campaign_name;
  const cRev = hasRevenue ? ` (${money(row.contributed_revenue)})` : '';
  const eRev = hasRevenue ? ` (${money(row.exclusive_revenue)})` : '';
  if (c === 0) { return `${name} influenced no conversions in this range.`; }
  if (e === 0) { return sharedOnlySentence(row, hasRevenue); }
  if (e >= c) {
    return `Removing ${name} would result in a loss of ${c} ${plural(c)}${cRev} — all certain.`;
  }
  return `Removing ${name} would result in a loss of at least ${e} ${plural(e)}${eRev}, `
    + `and could rise to ${c} ${plural(c)}${cRev}.`;
};

// Rounded axis step so gridlines land on whole conversions, targeting ~6 intervals.
const niceStep = (total: number) => {
  const raw = total / 6;
  const pow = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 1))));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((c) => c >= raw);
  return Math.max(1, Math.round(step || raw));
};

export function ContributionChart({ data, isDarkTheme = false }: ContributionChartProps) {
  const theme = useMantineTheme();
  const muted = isDarkTheme ? '#909296' : '#868e96';
  const gridLine = isDarkTheme ? '#373a40' : '#e9ecef';
  const divider = isDarkTheme ? '#5c5f66' : '#adb5bd';
  const strongText = isDarkTheme ? '#e9ecef' : '#343a40';
  const lossText = isDarkTheme ? '#fcc419' : '#d9480f';

  if (!data || !data.eligible || data.total <= 0 || data.rows.length === 0) {
    return (
      <Box py={48}>
        <Text size="sm" color="dimmed" align="center">
          {data && data.reason ? data.reason : 'Select campaigns to load data'}
        </Text>
      </Box>
    );
  }

  const { total, rows, has_revenue: hasRevenue } = data;
  const excluded = Array.isArray(data.excluded) ? data.excluded : [];
  const step = niceStep(total);
  const ticks: number[] = [];
  for (let v = 0; v < total; v += step) { ticks.push(v); }

  // Gridlines are painted on each bar cell so they read as continuous columns across rows.
  const gridBackground = {
    backgroundImage: `linear-gradient(to right, ${gridLine} 1px, transparent 1px)`,
    backgroundSize: `${(step / total) * 100}% 100%`,
    backgroundPosition: 'left top',
  };

  const labelCol = 160;
  const panelCol = hasRevenue ? 410 : 240;
  const gridTemplate = `${labelCol}px minmax(220px, 1fr) ${panelCol}px`;

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
        <Box sx={{ minWidth: labelCol + panelCol + 260, display: 'grid', gridTemplateColumns: gridTemplate }}>
          {/* Header: total marker sits directly above the dashed rule */}
          <Box />
          <Flex align="flex-end" justify="flex-end" pb={4} pr={8}>
            <Text size="xs" weight={700} sx={{ color: strongText }}>
              {total} converting {total === 1 ? 'customer' : 'customers'}
            </Text>
          </Flex>
          <Flex align="flex-end" gap="md" pb={4} pl={16} sx={{ borderLeft: `1px dashed ${divider}` }}>
            <Text size="xs" weight={700} sx={{ flex: '1 1 auto', color: muted, letterSpacing: 0.6 }}>
              CONVERTING CUSTOMERS
            </Text>
            {hasRevenue && (
              <Text size="xs" weight={700} sx={{ flex: '0 0 170px', color: muted, letterSpacing: 0.6 }}>
                REVENUE
              </Text>
            )}
          </Flex>

          {rows.map((row, index) => {
            // Rows carry their name, so cycling past 8 campaigns never leaves a bar unidentified.
            const seriesColor = theme.colors[SERIES_COLORS[index % SERIES_COLORS.length]];
            const exclusiveColor = seriesColor[8];
            const sharedColor = seriesColor[isDarkTheme ? 3 : 2];
            const shared = row.contributed - row.exclusive;
            const empty = row.contributed === 0;
            const segment = {
              height: 34,
              padding: '0 8px',
              borderRadius: 5,
              // Proportion stays true; max-content only widens a segment enough to fit its number.
              minWidth: 'max-content',
              flexShrink: 0,
            };

            return [
              <Flex
                key={`l-${row.campaign_id}`}
                direction="column"
                justify="center"
                align="flex-end"
                pr="sm"
                sx={{ minHeight: 56 }}
              >
                <Text size="sm" weight={600} align="right" lineClamp={2} title={row.campaign_name}>
                  {row.campaign_name}
                </Text>
                {row.spend > 0 && (
                  <Text size="xs" sx={{ color: muted }}>
                    Spend {money(row.spend)}
                  </Text>
                )}
              </Flex>,

              <Tooltip
                key={`b-${row.campaign_id}`}
                multiline
                withArrow
                position="top"
                width={340}
                transitionProps={{ transition: 'pop', duration: 150 }}
                label={
                  <Box>
                    <Text size="xs" weight={700} mb={4}>
                      {`${row.campaign_name} · Contributed ${row.contributed} (${pctLabel(row)}) · Exclusive ${row.exclusive} · Shared ${shared}`}
                    </Text>
                    <Text size="xs">{lossSentence(row, hasRevenue)}</Text>
                  </Box>
                }
              >
                <Flex align="center" sx={{ minHeight: 56, cursor: 'default', ...gridBackground }}>
                  {empty ? (
                    <Text size="xs" sx={{ color: muted }}>
                      No converters
                    </Text>
                  ) : (
                    <Flex align="center" gap={4} sx={{ width: '100%' }}>
                      {row.exclusive > 0 && (
                        <Flex
                          align="center"
                          justify="center"
                          sx={{ ...segment, width: `${(row.exclusive / total) * 100}%`, backgroundColor: exclusiveColor }}
                        >
                          <Text size="sm" weight={700} sx={{ color: '#fff' }}>
                            {row.exclusive.toLocaleString()}
                          </Text>
                        </Flex>
                      )}
                      {shared > 0 && (
                        <Flex
                          align="center"
                          justify="center"
                          sx={{ ...segment, width: `${(shared / total) * 100}%`, backgroundColor: sharedColor }}
                        >
                          <Text size="sm" weight={700} sx={{ color: SHARED_INK }}>
                            {shared.toLocaleString()}
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  )}
                </Flex>
              </Tooltip>,

              <Flex
                key={`p-${row.campaign_id}`}
                align="center"
                gap="md"
                pl={16}
                sx={{ minHeight: 56, borderLeft: `1px dashed ${divider}` }}
              >
                <Box sx={{ flex: '1 1 auto' }}>
                  <Text size="xs" sx={{ color: muted }}>
                    <Text component="span" size="md" weight={700} sx={{ color: strongText }}>
                      {pctLabel(row)}
                    </Text>{' '}
                    of converting customers
                  </Text>
                  {!empty && (
                    <Text size="xs" sx={{ color: muted }}>
                      Potential loss:{' '}
                      <Text component="span" weight={700} sx={{ color: lossText }}>
                        {row.exclusive === 0
                          ? `up to ${row.contributed}`
                          : row.exclusive >= row.contributed
                            ? row.contributed
                            : `${row.exclusive}–${row.contributed}`}
                      </Text>{' '}
                      {plural(row.contributed)}
                    </Text>
                  )}
                </Box>
                {hasRevenue && (
                  <Box sx={{ flex: '0 0 170px' }}>
                    <Text size="sm" weight={700} sx={{ color: strongText }}>
                      {money(row.contributed_revenue)}
                    </Text>
                    {/* Same wording and rules as the conversions line: a range, one certain figure, or "up to". */}
                    <Text size="xs" sx={{ color: muted }}>
                      Potential loss:{' '}
                      <Text component="span" weight={700} sx={{ color: lossText }}>
                        {row.exclusive === 0
                          ? `up to ${money(row.contributed_revenue)}`
                          : row.exclusive >= row.contributed
                            ? money(row.contributed_revenue)
                            : `${money(row.exclusive_revenue)}–${money(row.contributed_revenue)}`}
                      </Text>
                    </Text>
                  </Box>
                )}
              </Flex>,
            ];
          })}

          {/* Axis */}
          <Box />
          <Box sx={{ position: 'relative', height: 22, borderTop: `1px solid ${gridLine}` }}>
            {ticks.map((t) => (
              <Text
                key={`t-${t}`}
                size="xs"
                sx={{
                  position: 'absolute',
                  left: `${(t / total) * 100}%`,
                  transform: 'translateX(-50%)',
                  paddingTop: 4,
                  color: muted,
                }}
              >
                {t}
              </Text>
            ))}
          </Box>
          <Box sx={{ borderLeft: `1px dashed ${divider}` }} />
        </Box>
      </Box>

      {excluded.length > 0 && (
        <Text size="xs" color="dimmed" mt="md">
          {`Not shown: ${excluded.join(', ')} (${excluded.length === 1 ? "doesn't" : "don't"} have the ${data.goal_name} goal).`}
        </Text>
      )}

      <Text size="xs" color="dimmed" mt="md">
        {NOTE}
      </Text>

      <Flex
        gap="xl"
        wrap="wrap"
        align="center"
        mt="sm"
        pt="sm"
        sx={{ borderTop: `1px solid ${gridLine}` }}
      >
        <Flex gap={8} align="center" sx={{ maxWidth: 400 }}>
          <Box sx={{ flex: '0 0 auto', width: 24, height: 13, borderRadius: 3, backgroundColor: isDarkTheme ? '#5c6370' : '#495057' }} />
          <Text size="xs" color="dimmed">
            <b>Darker = Exclusive</b>: visited only this campaign (certain loss if removed)
          </Text>
        </Flex>
        <Flex gap={8} align="center" sx={{ maxWidth: 400 }}>
          <Box sx={{ flex: '0 0 auto', width: 24, height: 13, borderRadius: 3, backgroundColor: isDarkTheme ? '#a5acb8' : '#ced4da' }} />
          <Text size="xs" color="dimmed">
            <b>Lighter = Shared</b>: also visited other selected campaigns
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
}
